import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import FormInput from '../FormInput/FormInput.jsx';
import Validate from './Validate/Validate';
import {
    childrenToArray,
    childIsInstanceOf,
    assertChildHasKey,
    augmentChildEvent,
} from './utils/formUtils';

import styles from './Form.scss';

const DEFAULT_VALIDATE_STATE = {
    isValid: false,
};

export default class Form extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        hasError: PropTypes.bool,
        inputClass: PropTypes.string,
        inputWithMessageClass: PropTypes.string,
        defaultValues: PropTypes.object,
        onFocus: PropTypes.func,
        onChange: PropTypes.func,
        onValidate: PropTypes.func,
        onSubmit: PropTypes.func,
        onSuccess: PropTypes.func,
        onFail: PropTypes.func,
    };

    static defaultProps = {
        className: '',
        hasError: false,
        inputClass: '',
        inputWithMessageClass: '',
        defaultValues: {},
        onFocus() {},
        onChange() {},
        onValidate() {},
        onSubmit() {},
        onSuccess() {},
        onFail() {},
        reactInputComponent: FormInput,
    };

    constructor(props) {
        super(props);
        this.state = {
            forceShowValidations: false,
            resetValidations: false,
            children: this._getChildrenState(this.props.children, true),
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.hasError && !this.props.hasError) {
            this.setState(() => ({ resetValidations: true }));
        }

        if (nextProps.children === this.props.children) {
            return;
        }

        const newChildrenKeys = this._getChildrenKeys(nextProps.children);

        this.setState((oldState) => {
            const oldChildren = oldState.children;
            const newChildrenState = newChildrenKeys.reduce((obj, key) => {
                if (oldChildren.hasOwnProperty(key)) {
                    return { ...obj, [key]: oldChildren[key] };
                }
                return { ...obj, [key]: this._initializeChild(key) };
            }, {});

            return {
                children: newChildrenState,
            };
        });
    }

    get _children() {
        if (!this.state) {
            return {};
        }
        return this.state.children;
    }

    get _isValid() {
        return Object.values(this._children).every((child) => child.isValid);
    }

    get _values() {
        return Object.entries(this._children)
            .reduce((obj, [key, child]) => {
                return { ...obj, [key]: child.value };
            }, {});
    }

    _getChildrenState(children) {
        return childrenToArray(children)
            .filter(this._filterNonValidates)
            .reduce((prevChildState, child) => {
                const childState = this._getChildState(child);
                return { ...prevChildState, ...childState };
            }, {});
    }

    _getChildrenKeys(children) {
        return childrenToArray(children)
            .filter(this._filterNonValidates)
            .map((child) => this._getKey(child));
    }

    _filterNonValidates(child) {
        if (!child) {
            return false;
        }

        return childIsInstanceOf(child, Validate);
    }

    _getChildState(child) {
        const key = this._getKey(child);
        let childState = this._children[key] || this._initializeChild(key);

        return { [key]: childState };
    }

    _initializeChild(key) {
        return {
            ...DEFAULT_VALIDATE_STATE,
            value: this.props.defaultValues[key] || '',
        };
    }

    _augmentChildren() {
        return childrenToArray(this.props.children)
            .map((child) => {
                if (!child) {
                    return child;
                }

                if (childIsInstanceOf(child, Validate)) {
                    return this._augmentValidate(child);
                }
                return child;
            });
    }

    _getKey(child) {
        assertChildHasKey(child);
        return child.key;
    }

    _augmentValidate(validate) {
        const { inputClass, inputWithMessageClass } = this.props;
        let newProps = this._newProps(validate, inputClass, inputWithMessageClass);

        return React.cloneElement(validate, newProps);
    }

    _newProps(child, inputClass, inputWithMessageClass) {
        const key = this._getKey(child);
        const value = this._children[key] && this._children[key].value;

        return {
            onFocus: augmentChildEvent(this._onFocus, child.props.onFocus),
            onChange: augmentChildEvent(this._onChange, child.props.onChange, key),
            value: child.props.value || value,
            onValidate: augmentChildEvent(this._onValidate, child.props.onValidate, key),
            forceShowValidation: this.state.forceShowValidations,
            resetValidation: this.state.resetValidations,
            inputClass: classNames(
                styles.input,
                child.props.inputClass,
                inputClass,
            ),
            inputWithMessageClass: classNames(
                styles.inputWithMessage,
                child.props.inputWithMessageClass,
                inputWithMessageClass,
            ),
        };
    }

    _onValidate = (isValid, key) => {
        this.setState((oldState) => {
            const oldChildren = oldState.children;
            const newChild = { ...oldChildren[key], isValid };
            const newChildren = { ...oldChildren, [key]: newChild };
            return { children: newChildren };
        });
        this.props.onValidate(this._isValid);
    }

    _onFocus = (e) => {
        const {
            forceShowValidations,
            resetValidations,
        } = this.state;

        if (forceShowValidations) {
            this.setState(() => ({ forceShowValidations: false }));
        }

        if (resetValidations) {
            this.setState(() => ({ resetValidations: false }));
        }

        this.props.onFocus(e);
    }

    _onChange = (e, key) => {
        // Since form submit errors can happen async, we need
        // check for a reset on change as well
        if (this.state.resetValidations) {
            this.setState(() => ({ resetValidations: false }));
        }

        const value = e.target.value;
        this.setState((oldState) => {
            const oldChildren = oldState.children;
            const newChild = { ...oldChildren[key], value };
            const newChildren = { ...oldChildren, [key]: newChild };
            return { children: { ...newChildren } };
        }, () => {
            this.props.onChange(this._values);
        });
    }

    _onSubmit = (e) => {
        e.preventDefault();
        this._blurFocusedInput();

        this.setState(
            () => ({ forceShowValidations: true }),
            () => this._submitForm(),
        );
    }

    _blurFocusedInput() {
        document.activeElement.blur();
    }

    _submitForm() {
        this.props.onSubmit(this._values);

        if (this._isValid) {
            return this.props.onSuccess(this._values);
        }

        this.props.onFail(this._values);
    }

    render() {
        return (
            <form
                className={classNames(styles.formContainer, this.props.className)}
                onSubmit={this._onSubmit}
            >
                {this._augmentChildren()}
            </form>
        );
    }
}
